import { type ChangeEvent, forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { InputText, type InputTextProps } from '@ev-tech/ui-core-components/InputText';
import { type Language } from '@ev-tech/search-modules-api';

export const FormattedNumberInput = forwardRef<HTMLInputElement, InputTextProps & { language?: Language }>(
  ({ language, onChange, onBlur, value, ...props }, forwardedRef) => {
    const ref = useRef<HTMLInputElement>(null);

    useImperativeHandle(forwardedRef, () => ref.current as HTMLInputElement);

    const formatter = useMemo(
      () =>
        new Intl.NumberFormat(language, {
          maximumFractionDigits: 0,
          useGrouping: true,
        }),
      [language],
    );

    const delimiter = useMemo(() => formatter.format(12_345).replaceAll(/\d/g, '')[0] || ',', [formatter]);

    const getRawPosition = useCallback(
      (formattedValue: string, cursorPosition: number): number => {
        const valueBeforeCursor = formattedValue.slice(0, cursorPosition);
        const delimitersBeforeCursor = (valueBeforeCursor.match(new RegExp(`\\${delimiter}`, 'g')) || []).length;
        return cursorPosition - delimitersBeforeCursor;
      },
      [delimiter],
    );

    const getFormattedPosition = useCallback(
      (formattedValue: string, rawPosition: number): number => {
        let delimitersCount = 0;
        const formattedPosition = rawPosition;

        // iterate formatted value until the cursor position to find the number of delimiters
        for (let i = 0; i < formattedValue.length && i < delimitersCount + formattedPosition; i++) {
          if (formattedValue[i] === delimiter) {
            delimitersCount++;
          }
        }

        return formattedPosition + delimitersCount;
      },
      [delimiter],
    );

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>): void => {
        const cursorPosition = e.target.selectionStart;
        const inputValue = e.target.value;

        // Allow only digits and group delimiter
        const validChars = new RegExp(`[^\\d${delimiter}]`);
        if (validChars.test(inputValue)) {
          return;
        }

        const rawValue = inputValue.replaceAll(/\D/g, '');
        const numericValue = rawValue === '' ? 0 : Number.parseInt(rawValue, 10);

        // TODO: refactor to only emit the raw value
        // incase anyone come accross this code, please refactor it to only emit the raw value
        onChange?.({
          target: {
            ...e.target,
            value: rawValue,
          },
        } as ChangeEvent<HTMLInputElement>);

        if (cursorPosition !== null && ref.current) {
          const rawPos = getRawPosition(inputValue, cursorPosition);
          // update cursor position after next frame
          requestAnimationFrame(() => {
            const formattedValue = formatter.format(numericValue);
            const newCursorPos = getFormattedPosition(formattedValue, rawPos);
            ref.current?.setSelectionRange(newCursorPos, newCursorPos);
          });
        }
      },
      [delimiter, formatter, getFormattedPosition, getRawPosition, onChange],
    );

    return (
      <InputText
        {...props}
        ref={ref}
        type="text"
        inputMode="numeric"
        onChange={handleChange}
        value={value ? formatter.format(Number(value)) : ''}
      />
    );
  },
);

FormattedNumberInput.displayName = 'FormattedNumberInput';
